import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { recoverFromStaleChunk } from '@/utils/chunk-recovery'

const RETRY_DELAY_MS = 400
const MAX_RETRIES = 2

async function importWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): Promise<{ default: T }> {
  let lastError: unknown

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      return await factory()
    } catch (error) {
      lastError = error
      if (attempt < MAX_RETRIES) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, RETRY_DELAY_MS * (attempt + 1))
        })
      }
    }
  }

  if (recoverFromStaleChunk(lastError)) {
    return new Promise(() => {
      /* Page is reloading — keep Suspense fallback visible. */
    })
  }

  throw lastError
}

const routeFactories: Array<() => Promise<unknown>> = []

/** Lazy route loader with retry + stale-chunk recovery after deploys. */
export const lazyRoute = ((factory) => {
  routeFactories.push(factory)
  return lazy(() => importWithRetry(factory))
}) as typeof lazy

let prefetchStarted = false

/**
 * Warm every route chunk once the app is idle, one at a time, so opening a
 * module on a phone doesn't wait on a network round trip (the old page stays
 * up meanwhile, but a slow chunk still felt like lag). Skipped on data-saver
 * / 2G. Failures are ignored — the real navigation retries on its own.
 */
export function prefetchRouteChunks() {
  if (prefetchStarted || typeof window === 'undefined') return
  prefetchStarted = true
  const connection = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string }
  }).connection
  if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType ?? '')) return

  const idle = (cb: () => void) => {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(cb, { timeout: 4000 })
    else globalThis.setTimeout(cb, 300)
  }

  const queue = [...routeFactories]
  const next = () => {
    const factory = queue.shift()
    if (!factory) return
    factory()
      .catch(() => {})
      .finally(() => idle(next))
  }
  window.setTimeout(() => idle(next), 2500)
}

export type { LazyExoticComponent }
